import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getIsRefreshing } from '@perpay-web/authentication/selectors';
import { authentication } from '@perpay-web/authentication/singleton';
import { analyticsPartnerHostedOnboardingOpenPinwheelModal } from '@perpay-web/fintech/actions/analytics/partnerHostedOnboarding';
import { refreshAccessToken } from '@perpay-web/fintech/actions/authentication';
import { verifyPartnerOnboardingCode } from '@perpay-web/fintech/actions/entities/partnerOnboarding';
import CardApplication from '@perpay-web/fintech/components/screens/CardApplication/CardApplication';
import { getCardApplicationStepFromStatus } from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { cardApplicationSetSteps } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import {
    PARTNER_ONBOARDING_CARD,
    WELCOME_OFFER_CODES,
} from '@perpay-web/fintech/constants/partnerOnboardingCodes';
import {
    getApplicationStatus,
    getApplicationUuid,
} from '@perpay-web/fintech/dataModules/fetchCardAccountApplicationForOnboarding';
import {
    fetchCardAccountsDataModule,
    getHasCardAccount,
} from '@perpay-web/fintech/dataModules/fetchCardAccounts';
import {
    fetchUserInfoDataModule,
    getAddress,
    getDateOfBirth,
    getEmail,
    getFirstName,
    getIsExistingUserWithPassword,
    getLastName,
    getSsnIsVerified,
    getUserAffiliateGroupCodes,
    getAffiliateOfferCode,
    getAffiliateOfferAmount,
    getVisibleSsn,
    isUserInPremiumCardGroup,
} from '@perpay-web/fintech/dataModules/fetchUserInfo';
import {
    getPartnerHostedApplicationUUID,
    getPartnerHostedStatus,
    partnerHostedCardApplicationDataModule,
} from '@perpay-web/fintech/dataModules/partnerHostedCardApplication';
import { useFetchCardApplicationStatusContext } from '@perpay-web/fintech/hooks/dataModule/useFetchCardApplicationStatusContext';
import {
    getOfferAmount,
    isMagicLinkRedirect,
    isPremiumCardApplicant as isPremiumCardApplicantSelector,
} from '@perpay-web/fintech/selectors/ui/partnerOnboarding';

const CardApplicationContainer = ({
    pho = false,
    existingApplicationUuid = null,
}) => {
    const dispatch = useDispatch();

    const searchParams = new URLSearchParams(window.location.search);
    const partnerCode =
        searchParams.get('partner') || authentication.getPartnerOnboardedCode();

    const isUserInfoLoading = useSelector(
        fetchUserInfoDataModule.getIsLoadingOrUnrequested,
    );
    const isTokenRefreshing = useSelector(getIsRefreshing);

    const isUserIdentityVerified = useSelector(getSsnIsVerified);
    const firstName = useSelector(getFirstName);
    const lastName = useSelector(getLastName);
    const dateOfBirth = useSelector(getDateOfBirth);
    const ssn = useSelector(getVisibleSsn);
    const applicationUuid =
        useSelector(getApplicationUuid) || existingApplicationUuid;
    const applicationStatus = useSelector(getApplicationStatus);
    const phoApplicationStatus = useSelector(getPartnerHostedStatus);
    const cardApplicationStatus = applicationStatus || phoApplicationStatus;
    const phoApplicationUuid = useSelector(getPartnerHostedApplicationUUID);
    const isPartnerOnboardingLoading = useSelector(
        partnerHostedCardApplicationDataModule.getIsLoadingOrUnrequested,
    );
    const currentUserAddress = useSelector(getAddress);

    const { dataRequest, dataReset } = useFetchCardApplicationStatusContext();

    const email = useSelector(getEmail);
    const isExistingUser = email.length > 0;
    const isExistingUserWithPassword = useSelector(
        getIsExistingUserWithPassword,
    );

    // TODO: remove affiliateGroupCodes usage in favor of affiliateOfferCode
    const affiliateGroupCodes = useSelector(getUserAffiliateGroupCodes);
    const affiliateOfferCode = useSelector(getAffiliateOfferCode);

    const welcomeAffiliateCode = affiliateGroupCodes.filter((code) =>
        WELCOME_OFFER_CODES.includes(code),
    )
        ? affiliateGroupCodes.filter((code) =>
              WELCOME_OFFER_CODES.includes(code),
          )[0]
        : null;

    const welcomeCode = affiliateOfferCode || welcomeAffiliateCode;

    const shouldRefreshAuth = useSelector(isMagicLinkRedirect);

    const isLoading =
        isUserInfoLoading ||
        isTokenRefreshing ||
        (pho &&
            existingApplicationUuid &&
            !applicationUuid &&
            isPartnerOnboardingLoading);

    const affiliateOfferAmount = useSelector(getAffiliateOfferAmount);
    const offerAmount = useSelector(getOfferAmount);
    const welcomeOffer = affiliateOfferAmount || offerAmount;

    const fetchActualUserInfo = useCallback(
        () => dispatch(fetchUserInfoDataModule.dataRequest()),
        [dispatch],
    );

    // check premium flow through query param validation or user group membership
    const hasValidPremiumCardUrlParams = useSelector(
        isPremiumCardApplicantSelector,
    );
    const userIsInPremiumCardGroup = useSelector(isUserInPremiumCardGroup);
    const isPremiumCardApplicant =
        hasValidPremiumCardUrlParams || userIsInPremiumCardGroup;

    const hasCardAccount = useSelector(getHasCardAccount);

    return (
        <CardApplication
            partnerCode={partnerCode}
            welcomeCode={welcomeCode}
            welcomeOffer={welcomeOffer}
            onMount={useCallback(() => {
                dispatch(fetchCardAccountsDataModule.dataRequest());
                if (shouldRefreshAuth) {
                    dispatch(refreshAccessToken());
                }
                if (applicationUuid || phoApplicationUuid) {
                    dataRequest(applicationUuid || phoApplicationUuid);
                } else if (pho) {
                    dispatch(
                        partnerHostedCardApplicationDataModule.dataRequest(),
                    );
                }
                if (cardApplicationStatus) {
                    dispatch(
                        cardApplicationSetSteps(
                            getCardApplicationStepFromStatus(
                                cardApplicationStatus,
                            ),
                        ),
                    );
                }
            }, [
                shouldRefreshAuth,
                applicationUuid,
                phoApplicationUuid,
                pho,
                cardApplicationStatus,
                dispatch,
                dataRequest,
            ])}
            fetchActualUserInfo={fetchActualUserInfo}
            onUnmount={useCallback(() => dataReset(), [dataReset])}
            onPinwheelModalMount={useCallback(
                () =>
                    dispatch(
                        analyticsPartnerHostedOnboardingOpenPinwheelModal(),
                    ),
                [dispatch],
            )}
            verifyPartnerCode={useCallback(() => {
                if (partnerCode && welcomeCode && !welcomeOffer)
                    dispatch(
                        verifyPartnerOnboardingCode({
                            partnerCode,
                            onboardingProduct: PARTNER_ONBOARDING_CARD,
                            welcomeCode,
                        }),
                    );
            }, [dispatch, partnerCode, welcomeCode, welcomeOffer])}
            isLoading={isLoading}
            isExistingUser={isExistingUser}
            isExistingUserWithPassword={isExistingUserWithPassword}
            isUserIdentityVerified={isUserIdentityVerified}
            firstName={firstName}
            lastName={lastName}
            dateOfBirth={dateOfBirth}
            ssn={ssn}
            applicationUuid={applicationUuid || phoApplicationUuid}
            currentUserAddress={currentUserAddress}
            isPremiumCardApplicant={isPremiumCardApplicant}
            hasCardAccount={hasCardAccount}
        />
    );
};

export default CardApplicationContainer;
